import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { post } from "@/lib/api";
import { highlightRect, highlightSpan, pageOfPosition } from "@/lib/documents";
import type { DocumentPreview } from "@/lib/types";
import { cn } from "@/lib/utils";

const REFETCH_AFTER_SAVE_MS = 400;

/** The rendered PDF page by page; the position being edited glows like a highlighter. */
export function PreviewPane({ id, updatedAt, highlighted }: { id: number; updatedAt: string; highlighted: number | null }) {
  // Wait for a pause after a save; the old image stays until the new one is there.
  const [version, setVersion] = useState(updatedAt);
  useEffect(() => {
    const t = setTimeout(() => setVersion(updatedAt), REFETCH_AFTER_SAVE_MS);
    return () => clearTimeout(t);
  }, [updatedAt]);
  const preview = useQuery({
    queryKey: ["preview", id, version],
    queryFn: () => post<DocumentPreview>(`/api/documents/${id}/preview`),
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
  const data = preview.data;

  const [chosen, setChosen] = useState(1);
  const pageCount = data ? Math.max(1, data.pages.length) : 1;
  // A highlighted position on another page flips the preview there, so the mark is always in view.
  const target = highlighted !== null && data ? pageOfPosition(data.boxes, highlighted) : null;
  const page = Math.min(target ?? chosen, pageCount);

  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const span = highlightSpan(width);
  const marks = highlighted !== null && data ? data.boxes.filter((b) => b.positionId === highlighted && b.page === page) : [];
  const failed = data?.error ?? (preview.isError ? preview.error.message : null);

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border bg-card p-3">
      <div className="flex items-center gap-2">
        <h2 className="text-[0.8125rem] font-semibold">Vorschau</h2>
        <span className="text-xs text-muted-foreground">so geht es raus</span>
        {pageCount > 1 && (
          <div role="group" aria-label="Seite" className="ml-auto flex gap-1">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={page === n}
                onClick={() => setChosen(n)}
                className={cn(
                  "h-6.5 min-w-6.5 rounded-md border px-2.5 text-xs outline-none transition-colors duration-150 focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none pointer-coarse:h-11 pointer-coarse:min-w-11",
                  page === n ? "border-primary bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground",
                )}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>
      {failed && (
        <p role="alert" className="text-[0.8125rem] text-destructive">
          Vorschau nicht verfügbar: {failed}{" "}
          <a href={`/api/documents/${id}/pdf`} target="_blank" rel="noopener" className="whitespace-nowrap text-foreground underline underline-offset-4">PDF öffnen</a>
        </p>
      )}
      <div
        ref={frame}
        aria-busy={preview.isFetching}
        className="relative aspect-[595.28/841.89] w-full overflow-hidden rounded-[3px] bg-white shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_2px_6px_rgb(0_0_0/0.08),0_12px_24px_-12px_rgb(0_0_0/0.18)]"
      >
        {data && data.pages[page - 1] ? (
          <img src={`data:image/png;base64,${data.pages[page - 1]}`} alt={`Vorschau Seite ${page} von ${pageCount}`} className="absolute inset-0 size-full" />
        ) : (
          !failed && <div aria-hidden className="absolute inset-0 animate-pulse bg-muted motion-reduce:animate-none" />
        )}
        {width > 0 &&
          marks.map((b) => {
            const r = highlightRect(b, width);
            return (
              <div
                key={`${b.page}-${b.top}`}
                aria-hidden
                data-testid="highlighter"
                className="pointer-events-none absolute rounded-[2px] bg-hl mix-blend-multiply [@media(prefers-color-scheme:dark)]:mix-blend-normal"
                style={{ top: r.top, height: r.height, ...span }}
              />
            );
          })}
      </div>
    </div>
  );
}
